import { useEffect, useRef } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';

const values = [
  {
    icon: 'ri-lock-2-line',
    title: 'Confidencialidade',
    desc: 'O sigilo é o alicerce de toda relação com nossos clientes. Cada informação é tratada com absoluta discrição.',
  },
  {
    icon: 'ri-brain-line',
    title: 'Inteligência Estratégica',
    desc: 'Tomadas de decisão embasadas em análise profunda de mercado, dados e expertise acumulada ao longo de anos.',
  },
  {
    icon: 'ri-global-line',
    title: 'Conexões Globais',
    desc: 'Uma rede selecionada de parceiros e especialistas nos principais centros financeiros do mundo.',
  },
  {
    icon: 'ri-trophy-line',
    title: 'Excelência',
    desc: 'Comprometimento inabalável com os mais altos padrões de qualidade em cada entrega.',
  },
];

const timeline = [
  { year: '2018', title: 'Fundação', desc: 'LOPES BUSINESS inicia suas operações com foco em consultoria esportiva e estruturação patrimonial.' },
  { year: '2019', title: 'Expansão Internacional', desc: 'Primeiras operações internacionais e formação da rede global de parceiros estratégicos.' },
  { year: '2021', title: 'Operações com Cripto', desc: 'Incorporação de operações com criptomoedas e ativos digitais ao portfólio de serviços.' },
  { year: '2023', title: 'Grandes Eventos', desc: 'Lançamento da vertical de planejamento e gestão de grandes eventos esportivos e corporativos.' },
  { year: '2025', title: 'Consolidação Global', desc: 'Presença consolidada nos principais mercados internacionais com estrutura de family office global.' },
];

const SCHEMA_SOBRE = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'AboutPage',
      '@id': `${import.meta.env.VITE_SITE_URL}/sobre/#webpage`,
      url: `${import.meta.env.VITE_SITE_URL}/sobre/`,
      name: 'Sobre a LOPES BUSINESS | Consultoria Esportiva e Blindagem Patrimonial',
      description:
        'Conheça a história, missão e valores da LOPES BUSINESS. Quase uma década de experiência em consultoria esportiva, blindagem patrimonial e operações financeiras internacionais.',
      inLanguage: 'pt-BR',
      isPartOf: { '@id': `${import.meta.env.VITE_SITE_URL}/#website` },
      about: { '@id': `${import.meta.env.VITE_SITE_URL}/#organization` },
    },
    {
      '@type': 'Organization',
      '@id': `${import.meta.env.VITE_SITE_URL}/#organization`,
      name: 'LOPES BUSINESS',
      url: import.meta.env.VITE_SITE_URL,
      foundingDate: '2018',
      description:
        'Consultoria estratégica esportiva, blindagem patrimonial e operações financeiras internacionais para atletas e empresários de alta performance.',
      areaServed: 'Worldwide',
      knowsAbout: [
        'Consultoria Esportiva',
        'Blindagem Patrimonial',
        'Operações Financeiras Internacionais',
        'Criptomoedas',
        'Family Office',
        'Grandes Eventos',
      ],
    },
  ],
};

const SobrePage = () => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    const id = 'schema-sobre';
    let script = document.getElementById(id) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.id = id;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(SCHEMA_SOBRE);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('opacity-100', 'translate-y-0');
            entry.target.classList.remove('opacity-0', 'translate-y-10');
          }
        });
      },
      { threshold: 0.1 }
    );
    const elements = ref.current?.querySelectorAll('.reveal');
    elements?.forEach((el) => observer.observe(el));
    return () => {
      observer.disconnect();
      const el = document.getElementById(id);
      if (el) el.remove();
    };
  }, []);

  return (
    <div className="bg-[#0B0B0D] min-h-screen" ref={ref}>
      <Navbar />

      {/* Page Hero */}
      <section className="relative pt-40 pb-24 overflow-hidden"
        style={{ background: 'linear-gradient(180deg, #0F172A 0%, #0B0B0D 100%)' }}
      >
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/c1985699aa26c79f7004c1b211cc4ed3.jpg"
            alt="LOPES BUSINESS Sobre"
            className="w-full h-full object-cover object-top opacity-10"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0F172A]/80 to-[#0B0B0D]"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <span className="font-sans text-xs font-medium tracking-[0.3em] uppercase text-[#06B6D4] block mb-4 reveal opacity-0 translate-y-10 transition-all duration-700">
            Quem Somos
          </span>
          <h1 className="font-serif text-5xl md:text-7xl font-light text-white mb-6 reveal opacity-0 translate-y-10 transition-all duration-700 delay-100"
            style={{ textShadow: '0 0 60px rgba(30,58,138,0.4)' }}
          >
            Sobre a <span className="italic text-[#C9A96E]">Lopes Business</span>
          </h1>
          <p className="font-sans text-base md:text-lg font-light text-[#D1D5DB]/70 max-w-2xl mx-auto leading-relaxed reveal opacity-0 translate-y-10 transition-all duration-700 delay-200">
            Quase uma década de experiência construindo soluções estratégicas de elite
            para atletas e empresários de alta performance.
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=elegant%20executive%20office%20interior%20dark%20sophisticated%20premium%20luxury%20workspace%20corporate%20high%20end%20furniture%20dark%20wood%20marble%20global%20business%20atlas%20world%20map%20prestige%20professional%20environment&width=700&height=550&seq=sobre-img-01&orientation=landscape"
                alt="LOPES BUSINESS - Consultoria Estratégica"
                className="w-full h-[480px] object-cover object-top rounded-2xl"
                style={{ boxShadow: '0 0 60px rgba(30,58,138,0.2)' }}
              />
            </div>
            <div className="flex flex-col gap-8">
              <div className="reveal opacity-0 translate-y-10 transition-all duration-700">
                <span className="font-sans text-xs font-medium tracking-[0.3em] uppercase text-[#06B6D4] block mb-4">
                  Nossa Missão
                </span>
                <h2 className="font-serif text-4xl md:text-5xl font-light text-white leading-tight">
                  Transformar potencial em legado duradouro.
                </h2>
              </div>
              <div className="reveal opacity-0 translate-y-10 transition-all duration-700 delay-100">
                <p className="font-sans text-base font-light text-[#D1D5DB]/70 leading-relaxed">
                  A Lopes Business nasceu com uma missão clara: oferecer às pessoas de alta
                  performance as mesmas ferramentas que grandes grupos financeiros globais
                  utilizam para proteger e multiplicar seu patrimônio.
                </p>
              </div>
              <div className="reveal opacity-0 translate-y-10 transition-all duration-700 delay-200">
                <p className="font-sans text-base font-light text-[#D1D5DB]/70 leading-relaxed">
                  Com um modelo de atuação baseado em confidencialidade absoluta e inteligência
                  estratégica, construímos soluções personalizadas que atravessam fronteiras
                  e resistem ao tempo.
                </p>
              </div>
              <div className="reveal opacity-0 translate-y-10 transition-all duration-700 delay-300 flex items-center gap-6 pt-4">
                <div className="text-center">
                  <p className="font-serif text-4xl font-light text-[#C9A96E]">+7</p>
                  <p className="font-sans text-xs font-light tracking-widest uppercase text-[#D1D5DB]/50 mt-1">Anos</p>
                </div>
                <div className="w-px h-12 bg-[#1E3A8A]/40"></div>
                <div className="text-center">
                  <p className="font-serif text-4xl font-light text-[#C9A96E]">4</p>
                  <p className="font-sans text-xs font-light tracking-widest uppercase text-[#D1D5DB]/50 mt-1">Verticais</p>
                </div>
                <div className="w-px h-12 bg-[#1E3A8A]/40"></div>
                <div className="text-center">
                  <p className="font-serif text-4xl font-light text-[#C9A96E]">100%</p>
                  <p className="font-sans text-xs font-light tracking-widest uppercase text-[#D1D5DB]/50 mt-1">Confidencial</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 lg:py-32 bg-[#0F172A]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-20">
            <div className="reveal opacity-0 translate-y-10 transition-all duration-700">
              <span className="font-sans text-xs font-medium tracking-[0.3em] uppercase text-[#06B6D4] block mb-4">
                Fundamentos
              </span>
              <h2 className="font-serif text-5xl md:text-6xl font-light text-white"
                style={{ textShadow: '0 0 40px rgba(30,58,138,0.3)' }}
              >
                Nossos Valores
              </h2>
              <div className="w-16 h-px bg-[#C9A96E] mx-auto mt-6"></div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((val, i) => (
              <div
                key={i}
                className="reveal opacity-0 translate-y-10 transition-all duration-700 p-8 rounded-2xl border border-[#1E3A8A]/30 flex flex-col gap-5"
                style={{
                  background: 'rgba(11,11,13,0.8)',
                  transitionDelay: `${i * 100}ms`,
                }}
              >
                <div className="w-14 h-14 flex items-center justify-center rounded-full border border-[#C9A96E]/30"
                  style={{ background: 'rgba(201,169,110,0.05)' }}
                >
                  <i className={`${val.icon} text-[#C9A96E] text-2xl`}></i>
                </div>
                <div>
                  <h3 className="font-serif text-xl font-medium text-white mb-3">{val.title}</h3>
                  <p className="font-sans text-sm font-light text-[#D1D5DB]/60 leading-relaxed">{val.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-24 lg:py-32">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-20">
            <div className="reveal opacity-0 translate-y-10 transition-all duration-700">
              <span className="font-sans text-xs font-medium tracking-[0.3em] uppercase text-[#06B6D4] block mb-4">
                Nossa Trajetória
              </span>
              <h2 className="font-serif text-5xl md:text-6xl font-light text-white">
                Uma Década de <span className="italic text-[#C9A96E]">Excelência</span>
              </h2>
              <div className="w-16 h-px bg-[#C9A96E] mx-auto mt-6"></div>
            </div>
          </div>

          <div className="relative flex flex-col gap-0">
            <div className="absolute left-[88px] top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-[#1E3A8A]/50 to-transparent hidden md:block"></div>
            {timeline.map((item, i) => (
              <div
                key={i}
                className={`reveal opacity-0 translate-y-10 transition-all duration-700 flex gap-8 items-start pb-12`}
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div className="flex-shrink-0 w-24 text-right hidden md:block">
                  <span className="font-serif text-2xl font-light text-[#C9A96E]">{item.year}</span>
                </div>
                <div className="hidden md:flex flex-col items-center flex-shrink-0">
                  <div className="w-4 h-4 rounded-full border-2 border-[#C9A96E] mt-1.5"
                    style={{ background: '#0B0B0D', boxShadow: '0 0 12px rgba(201,169,110,0.4)' }}
                  ></div>
                </div>
                <div className="flex-1 pb-8 border-b border-[#1E3A8A]/20">
                  <span className="font-serif text-xl font-light text-[#C9A96E] md:hidden block mb-1">{item.year}</span>
                  <h3 className="font-sans text-base font-semibold text-white mb-2">{item.title}</h3>
                  <p className="font-sans text-sm font-light text-[#D1D5DB]/60 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#0F172A] border-t border-[#1E3A8A]/20">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <div className="reveal opacity-0 translate-y-10 transition-all duration-700">
            <h2 className="font-serif text-4xl md:text-5xl font-light text-white mb-6">
              Pronto para dar o próximo passo?
            </h2>
            <p className="font-sans text-base font-light text-[#D1D5DB]/60 mb-10 leading-relaxed">
              Nossa equipe está disponível para uma conversa confidencial sobre como podemos
              estruturar sua carreira e patrimônio.
            </p>
            <a
              href="https://wa.me/5547997236779"
              target="_blank"
              rel="nofollow noreferrer"
              className="inline-flex items-center gap-3 font-sans text-sm font-semibold tracking-widest uppercase bg-[#C9A96E] text-[#0B0B0D] px-12 py-4 rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap"
              style={{ boxShadow: '0 0 30px rgba(201,169,110,0.3)' }}
            >
              <i className="ri-whatsapp-line text-lg"></i>
              Fale com um Especialista
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default SobrePage;
